import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { RiEyeLine } from "react-icons/ri";

import File from "../../../newUXAssets/icons/File";
import DownloadSimple from "../../../newUXAssets/icons/DownloadSimple";
import useNotification from "../../../hooks/newUXNotification";
import {
  getNfseByContractService,
  simulateNfseByContractService,
} from "../../../service/nfse.service";
import { TitleBox } from "../styles";
import Table from "../ContractProductsServices/components/Table";
import {
  Container,
  HeaderActions,
  OutlineButton,
  ViewButton,
  EmptyState,
  EmptyIcon,
  EmptyMessage,
} from "./styles";

// Consulta publica da nota na Prefeitura de Sao Paulo. A env var existe so como escape se a
// prefeitura mudar o endereco; sem ela o padrao abaixo ja funciona.
const NFSE_CONSULT_BASE =
  process.env.REACT_APP_NFSE_CONSULT_URL ||
  "https://nfe.prefeitura.sp.gov.br/contribuinte/notaprint.aspx";

// "Baixar todas" fica fora do ar por decisao de produto (INFORMACP-4815): o download em lote
// entra em sprint futura. O handler, o botao e as traducoes seguem prontos; basta virar esta
// flag para true quando a funcionalidade for priorizada.
const DOWNLOAD_ALL_ENABLED = false;

// A consulta exige os tres identificadores da nota: ccm (inscricao municipal da Informa), nf
// (numero da NFS-e) e cod (codigo de verificacao). Sem qualquer um deles a URL nao resolve,
// entao nao montamos um link quebrado. URLSearchParams escapa os valores, que vem do Oracle
// via EIP.
const buildNfseConsultUrl = ({
  registrationNumber,
  nfseNumber,
  validationCode,
} = {}) => {
  if (
    !NFSE_CONSULT_BASE ||
    !registrationNumber ||
    !nfseNumber ||
    !validationCode
  ) {
    return "";
  }

  const params = new URLSearchParams({
    ccm: registrationNumber,
    nf: nfseNumber,
    cod: validationCode,
  });

  return `${NFSE_CONSULT_BASE}?${params.toString()}`;
};

const ContractServiceInvoices = ({ contractNumber }) => {
  const intl = useIntl();
  const notification = useNotification();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [simulating, setSimulating] = useState(false);
  // Quem decide e o backend, que ja conhece a flag de simulacao e o ambiente. Comeca false e so
  // liga se a resposta disser que sim, entao producao (e qualquer falha de leitura) esconde o
  // botao sem depender de configuracao no build do frontend.
  const [simulationAvailable, setSimulationAvailable] = useState(false);
  // Incrementado pela simulacao para recarregar a lista pelo mesmo caminho guardado do efeito,
  // em vez de duplicar a logica de fetch.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!contractNumber) {
      setInvoices([]);
      setError(false);
      setSimulationAvailable(false);
      return undefined;
    }

    // Descarta a resposta se o contrato mudar antes de ela chegar, para uma requisicao antiga
    // nao sobrescrever a lista do contrato atual.
    let active = true;

    const loadInvoices = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await getNfseByContractService(contractNumber);

        if (!active) return;

        setInvoices(response?.data?.nfseList ?? []);
        setSimulationAvailable(response?.data?.simulationAvailable === true);
      } catch (err) {
        if (!active) return;

        setInvoices([]);
        setSimulationAvailable(false);
        setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadInvoices();

    return () => {
      active = false;
    };
  }, [contractNumber, reloadToken]);

  const hasInvoices = invoices.length > 0;

  const handleDownloadAll = () => {
    notification.displayInfo(
      intl.formatMessage({ id: "contract_service_invoices.coming_soon" }),
      "nfse-download-all"
    );
  };

  const handleMissingData = () => {
    notification.displayError(
      intl.formatMessage({ id: "contract_service_invoices.missing_data" }),
      "nfse-missing-data"
    );
  };

  const handleSimulate = async () => {
    if (!contractNumber || simulating) return;

    setSimulating(true);

    try {
      await simulateNfseByContractService(contractNumber);

      notification.displaySuccess(
        intl.formatMessage({ id: "contract_service_invoices.simulate_success" }),
        "nfse-simulate"
      );

      setReloadToken((token) => token + 1);
    } catch (err) {
      notification.displayError(
        intl.formatMessage({ id: "contract_service_invoices.simulate_error" }),
        "nfse-simulate"
      );
    } finally {
      setSimulating(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <EmptyState>
          <EmptyMessage>
            {intl.formatMessage({ id: "contract_service_invoices.loading" })}
          </EmptyMessage>
        </EmptyState>
      );
    }

    if (error) {
      return (
        <EmptyState>
          <EmptyIcon>
            <File size="32" />
          </EmptyIcon>
          <EmptyMessage>
            {intl.formatMessage({ id: "contract_service_invoices.fetch_error" })}
          </EmptyMessage>
        </EmptyState>
      );
    }

    if (!hasInvoices) {
      return (
        <EmptyState>
          <EmptyIcon>
            <File size="32" />
          </EmptyIcon>
          <EmptyMessage>
            {intl.formatMessage({
              id: "contract_service_invoices.empty_message",
            })}
          </EmptyMessage>
        </EmptyState>
      );
    }

    const label = intl.formatMessage({
      id: "contract_service_invoices.view_note",
    });

    const columns = [
      {
        title: intl.formatMessage({
          id: "contract_service_invoices.column_document",
        }),
        dataIndex: "nfseNumber",
        key: "nfseNumber",
        render: (nfseNumber) =>
          intl.formatMessage(
            { id: "contract_service_invoices.document_number" },
            { number: nfseNumber }
          ),
      },
      {
        title: intl.formatMessage({
          id: "contract_service_invoices.column_action",
        }),
        key: "action",
        width: 200,
        render: (_, invoice) => {
          const consultUrl = buildNfseConsultUrl(invoice);

          // O portal da Prefeitura recusa ser embutido em iframe (X-Frame-Options), entao a
          // nota abre em nova aba. E um link de verdade, e nao window.open, para o usuario
          // poder abrir em nova janela, copiar o endereco e navegar pelo teclado.
          return consultUrl ? (
            <ViewButton
              as="a"
              href={consultUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <RiEyeLine size={16} />
              {label}
            </ViewButton>
          ) : (
            <ViewButton type="button" onClick={handleMissingData}>
              <RiEyeLine size={16} />
              {label}
            </ViewButton>
          );
        },
      },
    ];

    return (
      <Table
        dataSource={invoices}
        columns={columns}
        rowKey="nfseNumber"
        disabledPagination
        isDisabledSeach
      />
    );
  };

  return (
    <Container data-test="CONTRACT_SERVICE_INVOICES">
      <TitleBox>
        <File />
        <span>
          {intl.formatMessage({ id: "contract_service_invoices.title" })}
        </span>

        <HeaderActions>
          {/*
            Visivel so quando o backend informa que a simulacao esta disponivel — em producao
            vem false e o botao nao aparece. Fica visivel tambem com a lista vazia: simular a
            primeira nota do contrato e justamente o caso de uso principal em DEV/STAGE.
          */}
          {simulationAvailable && (
            <OutlineButton
              type="button"
              onClick={handleSimulate}
              disabled={simulating || !contractNumber}
              data-test="CONTRACT_SERVICE_INVOICES_SIMULATE"
            >
              {intl.formatMessage({
                id: simulating
                  ? "contract_service_invoices.simulating"
                  : "contract_service_invoices.simulate",
              })}
            </OutlineButton>
          )}

          {DOWNLOAD_ALL_ENABLED && hasInvoices && (
            <OutlineButton type="button" onClick={handleDownloadAll}>
              <DownloadSimple size="16" color="#1b1b1f" />
              {intl.formatMessage({
                id: "contract_service_invoices.download_all",
              })}
            </OutlineButton>
          )}
        </HeaderActions>
      </TitleBox>

      {renderContent()}
    </Container>
  );
};

export default ContractServiceInvoices;
